import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Paginate,
  SideModal,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeaderText from 'components/base/page-header-text';
import { ALL, MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { debounce, formatNumberWithCommas } from 'utils/functions';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { StockType } from 'models/stock.model';
import useDashboardContext from 'contexts/dashboard.context';
import { SocketIOConnection } from 'utils/socket-config';
import StockFilters, {
  EXPIRED,
  EXPIRING_SOON,
  InventoryQuantityType,
  SelectOption
} from './stock-filters';
import BatchStockTable from './batch-stock-table';
import { STORE_CONFIG, STORE_TYPE_LABELS, StoreTypeParam, normalizeStoreType } from './types';

import { MenuVerticalIcon } from 'cokitchen-icons';
import moment from 'moment';
import './styles.scss';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const TABLE_HEADERS = [
  'Item name',
  'Qty available (g)',
  'Min. stock level',
  'KG (per unit)',
  'Gram (per unit)'
];

const BATCH_EXPIRY_OPTIONS: SelectOption<InventoryQuantityType>[] = [
  {
    text: 'All',
    value: ALL
  },
  {
    text: 'Expiring soon items',
    value: EXPIRING_SOON
  },
  {
    text: 'Expired items',
    value: EXPIRED
  }
];

type StockItemsPageProps = {
  defaultStoreType: StoreTypeParam;
};

const StockItemsPage = ({ defaultStoreType }: StockItemsPageProps) => {
  const { storeType: storeTypeParam } = useParams();
  const storeType = normalizeStoreType(storeTypeParam, defaultStoreType);
  const storeConfig = STORE_CONFIG[storeType];

  const [searchValue, setSearchValue] = useState('');
  const [categoryFilter] = useState<string[]>(['All']);
  const [stockArray, setStockArray] = useState<StockType[]>([]);
  const [quantityFilter, setQuantityFilter] = useState<InventoryQuantityType>(ALL);
  const [isFetchingStock, setIsFetchingStock] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [batchView, setBatchView] = useState(false);
  const [expiryCounts, setExpiryCounts] = useState({ expiring_soon_count: 0, expired_count: 0 });
  const [activeBatch, setActiveBatch] = useState<StockType | null>(null);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );
  const navigate = useNavigate();

  const initSocket = useCallback(async () => {
    if (!activeStore?.id) return;
    if (storeType === 'prep-store') return;

    const conn = await SocketIOConnection.getConnection();
    const roomName = `main_store_batch:${activeStore?.id}`;

    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    });

    if (storeType === 'main-store') {
      conn.addEventListener(
        roomName,
        'kitchen_store_batch_updated',
        (data: { message: { id: string; price: number; quantity: number } }) => {
          if (!batchView) return;
          const { id, price, quantity: batchQuantity } = data.message;
          setStockArray((prev) =>
            prev.map((stock) =>
              stock.id === id ? { ...stock, price, quantity: batchQuantity } : stock
            )
          );
        }
      );
    }

    if (storeType === 'kitchen-store') {
      conn.addEventListener(
        roomName,
        'main_store_batch_updated',
        (data: { message: { id: string; price: number; quantity: number } }) => {
          const { id, price, quantity: batchQuantity } = data.message;
          setStockArray((prev) =>
            prev.map((stock) =>
              stock.id === id ? { ...stock, price, quantity: batchQuantity } : stock
            )
          );
        }
      );
    }

    conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
  }, [activeStore?.id, storeType, batchView]);

  useEffect(() => {
    initSocket();
  }, [initSocket]);

  useEffect(() => {
    if (!batchView) {
      setActiveBatch(null);
    }
  }, [batchView]);

  const normalizeCategoryFilter = (filters: string[]) => {
    if (filters.length === 0 || filters.includes('All')) {
      return ['All'];
    }
    return filters;
  };

  const fetchItemsInStock = useCallback(
    async (
      searchString: string,
      category: string[],
      quantityValue: InventoryQuantityType,
      current_page?: number,
      isBatchView?: boolean
    ) => {
      if (!activeStore?.id) return;

      try {
        setIsFetchingStock(true);
        const normalizedCategory = normalizeCategoryFilter(category);

        const endpoint = isBatchView
          ? storeConfig.batchesEndpoint?.(activeStore?.id)
          : storeConfig.itemsEndpoint(activeStore?.id);
        if (!endpoint) {
          setStockArray([]);
          setQuantity(0);
          setLastPage(1);
          return;
        }
        const params: Record<string, string | string[]> = {
          page: current_page ? String(current_page) : '1'
        };
        if (!normalizedCategory.includes('All')) {
          params.sort = normalizedCategory;
        }

        if (!isBatchView && quantityValue !== ALL) {
          params.min_quantity_filter = quantityValue.toLowerCase();
        } else if (isBatchView && quantityValue !== ALL) {
          params.status = quantityValue === EXPIRING_SOON ? 'expiring_soon' : 'expired';
        }

        if (searchString) {
          params[storeConfig.searchParam] = searchString;
        }

        const response = await apiService.get(endpoint, {
          params,
          version: 'v2'
        });

        const results =
          response?.data?.results || response?.data?.data?.results || response?.data?.data || [];
        const total = response?.data?.pagination?.total || results.length || 0;
        const pageSize = response?.data?.pagination?.page_size;

        setStockArray(results);
        setQuantity(Math.ceil(total));
        setLastPage(Math.max(1, Math.ceil(total / pageSize)));
      } catch (err: any) {
        toast(
          <ToastMessage
            message={err.response?.data?.message || 'Error fetching stock'}
            type='error'
          />
        );
      } finally {
        setIsFetchingStock(false);
      }
    },
    [activeStore?.id, batchView, storeConfig, storeType]
  );

  const debounceFetchItemsInStock = useMemo(() => debounce(fetchItemsInStock, 1000), []);

  useEffect(() => {
    setStockArray([]);
    setIsFetchingStock(true);
    debounceFetchItemsInStock(searchValue, categoryFilter, quantityFilter, currentPage, batchView);
  }, [
    searchValue,
    categoryFilter,
    quantityFilter,
    currentPage,
    batchView,
    debounceFetchItemsInStock
  ]);

  const fetchExpiryCounts = useCallback(async () => {
    if (!activeStore?.id) return;

    try {
      const endpoint = storeConfig.getExpiredItems;
      if (!endpoint) return;
      const res = await apiService.get(endpoint, {
        version: 'v2',
        params: {
          store_id: activeStore.id
        }
      });
      setExpiryCounts(res.data.data);
    } catch (err: any) {
      console.log(err);
    }
  }, [activeStore?.id, storeConfig, storeType]);

  useEffect(() => {
    fetchExpiryCounts();
  }, []);

  const handleUpdateDamaged = (stock: StockType) => {
    if (storeType === 'prep-store') return;
    const baseRoute =
      storeType === 'main-store'
        ? MAIN_STORE_ROUTE_KEYS.INVENTORY_STOCK
        : MAIN_STORE_ROUTE_KEYS.KITCHEN_INVENTORY_STOCK;
    if (stock?.ingredient?.id) {
      navigate(`${baseRoute}/edit/${stock.ingredient.id}_${stock.batch_no}`);
    }
  };

  const HeaderAction = () => {
    return (
      <Button
        onClick={() => {
          setStockArray([]);
          setQuantityFilter(ALL);

          setBatchView((prev) => !prev);
        }}
        variant='block'
        color='primary'
        className='px-6 py-2 text-sm'>
        {!batchView ? 'View batch details' : 'View item details'}
      </Button>
    );
  };
  const goToMinimumQuantity = () => {
    const isMainStore = storeType === 'main-store';
    const minStockRoute = isMainStore
      ? MAIN_STORE_ROUTE_KEYS.MAIN_STORE_MINIMUM_STOCK_SETTINGS
      : MAIN_STORE_ROUTE_KEYS.KITCHEN_MINIMUM_STOCK_SETTINGS;
    if (isMainStore) {
      navigate(minStockRoute);
      return;
    }
    const kitchenSearch =
      storeType === 'brand-chef-store'
        ? '?storeType=brand-chef-store'
        : storeType === 'prep-store'
        ? '?storeType=prep-store'
        : '';
    navigate(`${minStockRoute}${kitchenSearch}`);
  };

  return (
    <div className='inventory_stock'>
      <PageHeaderText
        title={`Item lists (${isFetchingStock ? '**' : quantity})`}
        description={`Manage and track the quantity of items in ${STORE_TYPE_LABELS[storeType]}`}>
        {HeaderAction()}
      </PageHeaderText>

      <StockFilters
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        quantityFilter={quantityFilter}
        setQuantityFilter={setQuantityFilter}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
        quantityOptions={batchView ? BATCH_EXPIRY_OPTIONS : undefined}
        quantityLabel={batchView ? 'Expiry' : undefined}
        showPagination={false}
      />

      <div className='mt-6 grid gap-4 md:grid-cols-3'>
        <div
          onClick={() => {
            setBatchView(true);
            setStockArray([]);

            setQuantityFilter(EXPIRING_SOON);
            setCurrentPage(1);
          }}
          className='rounded-lg border border-[#E5E6E6] bg-white px-4 py-3 shadow-sm cursor-pointer'>
          <p className='text-xs text-[#6F7376]'>Expiring soon</p>
          <p className='mt-1 text-2xl font-semibold text-[#2E3031]'>
            {formatNumberWithCommas(expiryCounts.expiring_soon_count)}
          </p>
          <p className='text-xs text-[#6F7376] pt-2'>View items {'>'}</p>
        </div>
        <div
          onClick={() => {
            setBatchView(true);
            setQuantityFilter(EXPIRED);
            setStockArray([]);
            setCurrentPage(1);
          }}
          className='rounded-lg border border-[#E5E6E6] bg-white px-4 py-3 shadow-sm cursor-pointer'>
          <p className='text-xs text-[#6F7376]'>Expired items</p>
          <p className='mt-1 text-2xl font-semibold text-[#E60A2B]'>
            {formatNumberWithCommas(expiryCounts.expired_count)}
          </p>
          <p className='text-xs text-[#6F7376] pt-2'>View items {'>'}</p>
        </div>
        {canWriteInventoryManagement && (
          <div
            onClick={() => {
              goToMinimumQuantity();
            }}
            className='rounded-lg border border-[#E5E6E6] bg-white px-4 py-5 shadow-sm cursor-pointer'>
            <p className='text-xs text-[#6F7376]'>Set minimum quantity</p>
            <p className='mt-1 text-sm text-[#6F7376]'>Set a baseline to track low stock items.</p>
            <span className='mt-2 inline-flex text-xs text-[#6F7376]'>View dashboard</span>
          </div>
        )}
      </div>

      <div className='mt-6'>
        {stockArray.length === 0 && !isFetchingStock ? (
          <div className='flex justify-center mt-3'>
            <p>No stock items found</p>
          </div>
        ) : (
          <div className='no-scroll-bar whitespace-nowrap pb-10'>
            {batchView ? (
              <>
                <BatchStockTable
                  data={stockArray}
                  loading={isFetchingStock}
                  actionRenderer={(stock) =>
                    storeType === 'prep-store' || !canWriteInventoryManagement ? (
                      <span className='text-gray-5'>-</span>
                    ) : (
                      <Dropdown
                        visible={activeActionId === stock.id}
                        onClickOutside={() => setActiveActionId(null)}
                        content={
                          <button
                            type='button'
                            onClick={() =>
                              setActiveActionId((prev) => (prev === stock.id ? null : stock.id))
                            }
                            className='h-7 w-7 rounded-full border border-[#E5E6E6] flex items-center justify-center text-gray-8 hover:bg-[#F2F2F3]'>
                            <MenuVerticalIcon className='text-gray-8' />
                          </button>
                        }>
                        <DropdownList className='z-[999] min-w-[200px]'>
                          <DropdownItem
                            onClick={() => {
                              setActiveActionId(null);
                              handleUpdateDamaged(stock);
                            }}>
                            Update damaged quantity
                          </DropdownItem>
                        </DropdownList>
                      </Dropdown>
                    )
                  }
                />
                <SideModal
                  isOpen={!!activeBatch}
                  onRequestClose={() => setActiveBatch(null)}
                  hideFooter>
                  <div>
                    <div className='mb-[40px]'>
                      <p className='font-medium text-[20px] pb-1'>Batch {activeBatch?.batch_no}</p>
                      <p className='text-[#6F7376] text-sm'>Manage procurement</p>
                    </div>
                    <div className='space-y-12'>
                      <Input
                        type='text'
                        label='Item name'
                        placeholder='e.g Lead Chef'
                        name='name'
                        className='w-full'
                        design='bordered'
                        value={activeBatch?.ingredient?.name || ''}
                        readOnly
                      />
                      <Input
                        type='text'
                        label='Qty Available'
                        placeholder='Quantity'
                        name='name'
                        className='w-full'
                        design='bordered'
                        value={activeBatch?.quantity ?? ''}
                        readOnly
                      />
                      <Input
                        type='text'
                        label='Grams(per unit)'
                        placeholder='Weight'
                        name='name'
                        className='w-full'
                        design='bordered'
                        value={`${Math.round(Number(activeBatch?.ingredient?.unit_weight) ?? 0)} g`}
                        readOnly
                      />
                      <Input
                        type='text'
                        label='Expiry Date'
                        placeholder='Expiry Date'
                        name='name'
                        className='w-full'
                        design='bordered'
                        value={
                          activeBatch?.expiration_date
                            ? moment(activeBatch?.expiration_date).format('MM/YY')
                            : '-'
                        }
                        readOnly
                      />
                    </div>
                  </div>
                </SideModal>
              </>
            ) : (
              <Table headers={TABLE_HEADERS} loading={isFetchingStock}>
                {stockArray?.map((stock) => {
                  const minQuantity = Number(stock?.min_quantity ?? 0);
                  const itemQuantity = Number(stock?.quantity ?? 0);
                  const isBelowMin = minQuantity > 0 && itemQuantity < minQuantity;
                  const minQuantityLabel =
                    minQuantity > 0 ? formatNumberWithCommas(minQuantity) : 'Not set';
                  const minQuantityClass =
                    minQuantity === 0 || isBelowMin ? 'text-red-5' : 'text-gray-10';
                  const unitWeight = Number(stock?.ingredient?.unit_weight ?? 0);
                  const unitWeightKg = unitWeight ? `${(unitWeight / 1000).toFixed(2)}kg` : '-';
                  const unitWeightGram = unitWeight
                    ? `${formatNumberWithCommas(unitWeight)}g`
                    : '-';

                  return (
                    <TableRow className='text-gray-12' key={stock.id}>
                      <TableData className='text-gray-10 font-medium'>
                        {stock?.ingredient?.name ?? 'No name '}
                      </TableData>
                      <TableData
                        className={`px-5 font-bold ${
                          itemQuantity <= Number(minQuantityLabel) ? ' text-red-5' : 'text-gray-5'
                        }`}>
                        {formatNumberWithCommas(itemQuantity)}
                      </TableData>
                      <TableData className={minQuantityClass}>
                        {formatNumberWithCommas(minQuantityLabel)}
                      </TableData>
                      <TableData className='px-5 text-gray-5'>{unitWeightKg}</TableData>
                      <TableData className='px-5 text-gray-5'>{unitWeightGram}</TableData>
                    </TableRow>
                  );
                })}
              </Table>
            )}
          </div>
        )}
      </div>
      {lastPage > 1 && (
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => setCurrentPage(e.selected + 1)}
        />
      )}
    </div>
  );
};

export default StockItemsPage;
